import { useRef, useState } from "react";
import { api, API_BASE, getEffectiveDepartment } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Download, RotateCcw } from "lucide-react";

/**
 * Minimal CSV bulk-importer for students.
 * Three steps: upload → preview → confirm → summary.
 */
export default function BulkImportStudents({ onImported }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sendWelcome, setSendWelcome] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setResult(null);
    setSendWelcome(false);
  };

  const close = (o) => {
    setOpen(o);
    if (!o) setTimeout(reset, 200);
  };

  const onPick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Solo se aceptan archivos .csv");
      return;
    }
    setFile(f);
  };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/users/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setAnalyzing(false);
    }
  };

  const dep = getEffectiveDepartment();
  const depNombre = dep === "dep_en" ? "We Speak English"
    : dep === "dep_es" ? "We Speak Spanish" : null;

  const confirm = async () => {
    if (!preview || preview.valid.length === 0) return;
    setImporting(true);
    try {
      const r = await api.post("/users/import/confirm", {
        students: preview.valid.map(({ name, email, whatsapp, country }) => ({
          name, email, whatsapp, country,
        })),
        send_welcome_email: sendWelcome,
        department_id: getEffectiveDepartment() || null,
      });
      setResult(r.data);
      setStep("done");
      if (onImported) onImported();
      toast.success(`${r.data.created} ${t("created_count")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      // Use direct fetch with credentials so cookie session_token is sent
      const res = await fetch(`${API_BASE}/api/users/import/template`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "students_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="bulk-import-open"
          onClick={() => setOpen(true)}
        >
          <Upload className="h-4 w-4 mr-1" /> {t("bulk_import_students")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="bulk-import-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("bulk_import_title")}</DialogTitle>
          <DialogDescription>{t("bulk_import_subtitle")}</DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600"
                 data-testid="bulk-import-department-note">
              {depNombre
                ? <>Los estudiantes se van a crear en <b>{depNombre}</b>.</>
                : <>Estás en la vista consolidada: los estudiantes quedarían en <b>Español</b>.
                   Si son de inglés, cambiá el selector de arriba a <b>Inglés</b> antes de subirlos.</>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
              className="text-blue-600 hover:text-blue-700"
            >
              <Download className="h-4 w-4 mr-1" /> {t("download_template")}
            </Button>

            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onPick(e.dataTransfer.files?.[0]);
              }}
              className="w-full border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 rounded-xl p-10 text-center transition-colors"
              data-testid="csv-dropzone"
            >
              <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{t("drop_csv_here")}</p>
              {file && (
                <p className="text-xs text-emerald-700 mt-3 font-medium">
                  {t("file_selected")}: {file.name}
                </p>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="csv-file-input"
              onChange={(e) => onPick(e.target.files?.[0])}
            />

            <div className="flex justify-end">
              <Button
                onClick={analyze}
                disabled={!file || analyzing}
                className="btn-brand"
                data-testid="analyze-csv-btn"
              >
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {t("analyze_csv")}
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label={t("rows_total")} value={preview.total} color="slate" testId="kpi-total" />
              <KPI
                label={t("rows_valid")}
                value={preview.valid.length}
                color="emerald"
                testId="kpi-valid"
              />
              <KPI
                label={t("rows_duplicate")}
                value={preview.duplicates.length}
                color="amber"
                testId="kpi-duplicates"
              />
              <KPI
                label={t("rows_invalid")}
                value={preview.invalid.length}
                color="red"
                testId="kpi-invalid"
              />
            </div>

            <Tabs defaultValue="valid">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="valid" data-testid="preview-tab-valid">
                  {t("rows_valid")} ({preview.valid.length})
                </TabsTrigger>
                <TabsTrigger value="duplicates" data-testid="preview-tab-duplicates">
                  {t("rows_duplicate")} ({preview.duplicates.length})
                </TabsTrigger>
                <TabsTrigger value="invalid" data-testid="preview-tab-invalid">
                  {t("rows_invalid")} ({preview.invalid.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="valid" className="mt-3">
                <RowTable rows={preview.valid} />
              </TabsContent>
              <TabsContent value="duplicates" className="mt-3">
                <RowTable rows={preview.duplicates} showReason />
              </TabsContent>
              <TabsContent value="invalid" className="mt-3">
                <RowTable rows={preview.invalid} showReason />
              </TabsContent>
            </Tabs>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <Checkbox
                id="send-welcome"
                checked={sendWelcome}
                onCheckedChange={(v) => setSendWelcome(!!v)}
                data-testid="send-welcome-checkbox"
              />
              <Label htmlFor="send-welcome" className="text-sm cursor-pointer">
                {t("send_welcome_emails")}
              </Label>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep("upload")}>
                <RotateCcw className="h-4 w-4 mr-1" /> {t("cancel")}
              </Button>
              <Button
                onClick={confirm}
                disabled={importing || preview.valid.length === 0}
                className="btn-brand"
                data-testid="confirm-import-btn"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : (
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                )}
                {preview.valid.length === 0
                  ? t("csv_no_valid_rows")
                  : `${t("confirm_import")} (${preview.valid.length})`}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-4" data-testid="import-summary">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <p className="text-xl font-bold text-emerald-900">
                {result.created} {t("created_count")}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-slate-900">{result.skipped}</div>
                <div className="text-xs text-slate-500">{t("skipped_count")}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-slate-900">{result.emails_sent}</div>
                <div className="text-xs text-slate-500">{t("emails_sent_count")}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-slate-900">{result.errors?.length || 0}</div>
                <div className="text-xs text-slate-500">{t("errors_count")}</div>
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-1">
                {result.errors.map((er, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <span>
                      <strong>{er.email}</strong> — {er.reason}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={reset} data-testid="import-again-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> {t("import_again")}
              </Button>
              <Button onClick={() => close(false)} className="btn-brand" data-testid="import-close-btn">
                {t("cancel")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, color = "slate", testId }) {
  const palette = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    red: "bg-red-50 text-red-900 border-red-200",
  }[color];
  return (
    <div className={`border rounded-lg p-3 text-center ${palette}`} data-testid={testId}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

function RowTable({ rows, showReason = false }) {
  const { t } = useI18n();
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-6">—</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>{t("name")}</TableHead>
            <TableHead>{t("email")}</TableHead>
            {showReason && <TableHead>{t("reason")}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs text-slate-500">{r.row}</TableCell>
              <TableCell className="text-sm">{r.name || "—"}</TableCell>
              <TableCell className="text-sm text-slate-600">{r.email || "—"}</TableCell>
              {showReason && (
                <TableCell className="text-xs text-amber-700">{r.reason}</TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
